import { useMemo } from 'react';
import { Accordion, Badge, Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Box, Paper, Typography } from '@mui/material';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, RadarComponent } from 'echarts/components';
import { BarChart, LineChart, RadarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { selectProjectResults } from '../../project-details/data-access/project.reducer';
import { gatherCollectionInsights, OntologyDomainRow } from './gatherCollectionInsights';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  RadarComponent,
  BarChart,
  LineChart,
  RadarChart,
  CanvasRenderer
]);

function riskToScore(level: string): number {
  const u = level.toUpperCase();
  if (u === 'CRITICAL') return 4;
  if (u === 'HIGH') return 3;
  if (u === 'MEDIUM') return 2;
  if (u === 'LOW') return 1;
  return 0;
}

function statusTone(value: string | null | undefined): 'success' | 'danger' | 'warning' | 'secondary' | 'info' {
  if (!value) return 'secondary';
  const v = value.toUpperCase();
  if (['PASS', 'OK', 'CLEAN', 'FAIR', 'LIKELY_GENUINE'].includes(v)) return 'success';
  if (['FAIL', 'AT_RISK', 'REJECT', 'HIGH'].includes(v)) return 'danger';
  if (['REVIEW', 'WARN', 'MEDIUM', 'NOT_COVERED', 'UNDER_REVIEW'].includes(v)) return 'warning';
  return 'info';
}

function riskBadgeVariant(level: string): string {
  const u = level.toUpperCase();
  if (u === 'HIGH' || u === 'CRITICAL') return 'danger';
  if (u === 'MEDIUM') return 'warning';
  if (u === 'LOW') return 'success';
  return 'secondary';
}

function SummaryCard({
  label,
  value,
  sub,
  tone
}: {
  label: string;
  value: string;
  sub?: string | null;
  tone: 'success' | 'danger' | 'warning' | 'secondary' | 'info';
}) {
  const accent: Record<typeof tone, string> = {
    success: 'var(--phoenix-success)',
    danger: 'var(--phoenix-danger)',
    warning: 'var(--phoenix-warning)',
    secondary: 'var(--phoenix-secondary-color)',
    info: 'var(--phoenix-info)'
  };
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'var(--phoenix-body-bg)'),
        borderLeft: `4px solid ${accent[tone]}`
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {sub}
        </Typography>
      ) : null}
    </Paper>
  );
}

function buildOntologyRadar(domains: OntologyDomainRow[]) {
  if (!domains.length) return null;
  const indicators = domains.map(d => ({
    name: d.risk_domain.length > 18 ? `${d.risk_domain.slice(0, 16)}…` : d.risk_domain,
    max: 4
  }));
  const values = domains.map(d => riskToScore(d.risk_level));
  return {
    tooltip: {},
    radar: {
      indicator: indicators,
      radius: '66%'
    },
    series: [
      {
        type: 'radar',
        areaStyle: { opacity: 0.12 },
        lineStyle: { width: 2 },
        data: [{ value: values, name: 'Risk profile' }]
      }
    ]
  };
}

function buildBiasVerdictBar(distribution: Record<string, number>) {
  const keys = Object.keys(distribution);
  if (!keys.length) return null;
  return {
    tooltip: { trigger: 'axis' },
    grid: { left: 48, right: 16, top: 24, bottom: 32 },
    xAxis: { type: 'category', data: keys },
    yAxis: { type: 'value', minInterval: 1 },
    series: [{ type: 'bar', data: keys.map(k => distribution[k]), itemStyle: { borderRadius: [4, 4, 0, 0] } }]
  };
}

function buildAbuseDriftLine(series: { eventIndex: number; abuseScorePercent: number }[]) {
  if (!series.length) return null;
  return {
    tooltip: { trigger: 'axis' },
    grid: { left: 48, right: 16, top: 24, bottom: 32 },
    xAxis: { type: 'category', data: series.map(s => `E${s.eventIndex}`) },
    yAxis: { type: 'value', name: '%', min: 0, max: 100 },
    series: [
      {
        type: 'line',
        smooth: true,
        symbolSize: 8,
        data: series.map(s => s.abuseScorePercent),
        areaStyle: { opacity: 0.08 }
      }
    ]
  };
}

function buildOwaspStatusBar(controls: { name: string; status: string }[]) {
  if (!controls.length) return null;
  const names = controls.map(c => (c.name.length > 22 ? `${c.name.slice(0, 20)}…` : c.name));
  const scores = controls.map(c => (c.status?.toUpperCase() === 'PASS' ? 1 : 0));
  return {
    tooltip: { trigger: 'axis' },
    grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
    xAxis: { type: 'value', min: 0, max: 1, axisLabel: { formatter: (v: number) => (v === 1 ? 'Pass' : 'Fail') } },
    yAxis: { type: 'category', data: names, inverse: true },
    series: [
      {
        type: 'bar',
        data: scores,
        itemStyle: {
          color: (params: { value: number }) => (params.value === 1 ? '#25b003' : '#e63757')
        }
      }
    ]
  };
}

const ProjectOverviewDashboard = () => {
  const projectResults = useSelector(selectProjectResults);
  const insights = useMemo(() => gatherCollectionInsights(projectResults), [projectResults]);

  const ontologyOption = useMemo(() => buildOntologyRadar(insights.ontologyDomains), [insights.ontologyDomains]);
  const biasBarOption = useMemo(
    () => buildBiasVerdictBar(insights.bias.verdictDistribution),
    [insights.bias.verdictDistribution]
  );
  const abuseLineOption = useMemo(() => buildAbuseDriftLine(insights.abuse.driftSeries), [insights.abuse.driftSeries]);
  const owaspBarOption = useMemo(() => buildOwaspStatusBar(insights.owasp.controls), [insights.owasp.controls]);

  const { kpis } = insights;

  const summaryRow: Array<{
    label: string;
    value: string;
    sub?: string | null;
    tone: 'success' | 'danger' | 'warning' | 'secondary' | 'info';
  }> = [
    {
      label: 'Ontology risk',
      value: kpis.ontologyOverall ?? '—',
      sub: 'Governance signals',
      tone: statusTone(kpis.ontologyOverall)
    },
    {
      label: 'Bias (dataset)',
      value: kpis.biasVerdict ?? '—',
      sub: kpis.biasScore != null ? `MicroEAI bias score ${kpis.biasScore.toFixed(2)}` : 'No aggregate score',
      tone: statusTone(kpis.biasVerdict)
    },
    {
      label: 'Abuse (LLM)',
      value: kpis.abuseVerdict ?? '—',
      sub: kpis.abuseIndex != null ? `Overall abuse index ${kpis.abuseIndex.toFixed(1)}` : 'No abuse index',
      tone: statusTone(kpis.abuseVerdict)
    },
    {
      label: 'Fairness (LLM)',
      value: kpis.fairnessLlmVerdict ?? '—',
      sub: kpis.fairnessLlmScore != null ? `Fairness score ${kpis.fairnessLlmScore.toFixed(2)}` : null,
      tone: statusTone(kpis.fairnessLlmVerdict)
    },
    {
      label: 'Compliance',
      value: `${kpis.externalCompliance ?? '—'} ext · ${kpis.internalCompliance ?? '—'} int`,
      sub: 'External / internal gates',
      tone: (kpis.externalCompliance === 'FAIL' || kpis.internalCompliance === 'FAIL' ? 'danger' : 'success') as
        | 'danger'
        | 'success'
    },
    {
      label: 'OWASP MLP',
      value: kpis.owaspStatus ?? '—',
      sub: `${kpis.owaspPassCount} pass · ${kpis.owaspFailCount} fail`,
      tone: statusTone(kpis.owaspStatus === 'AT_RISK' ? 'FAIL' : 'PASS')
    },
    {
      label: 'Audio authenticity',
      value: kpis.audioDecision ?? '—',
      sub: kpis.audioDeepfakeRisk != null ? `Deepfake risk ${kpis.audioDeepfakeRisk.toFixed(2)}` : 'No audio analysis',
      tone: statusTone(kpis.audioDecision)
    }
  ];

  const hasData = Boolean(projectResults);

  if (!hasData) {
    return (
      <div className="text-center py-5 text-muted">
        <h5 className="mb-2">No project results loaded</h5>
        <p className="mb-0">Select a project and wait for analysis data to appear here.</p>
      </div>
    );
  }

  return (
    <div className="pb-5">
      <div className="mb-4">
        <h4 className="mb-1">Project overview</h4>
        <p className="text-body-secondary mb-0 fw-semibold">
          Summary cards, interactive charts, and expandable sections for each analysis domain.
        </p>
      </div>

      <Row className="g-3 mb-4">
        {summaryRow.map(card => (
          <Col key={card.label} xs={12} sm={6} md={6} xl={4}>
            <SummaryCard {...card} />
          </Col>
        ))}
      </Row>

      <Row className="g-3 mb-2">
        <Col lg={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              height: 360
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Ontology domain risks
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Radar view of risk levels across governance domains (higher is worse).
            </Typography>
            {ontologyOption ? (
              <ReactEChartsCore echarts={echarts} option={ontologyOption} style={{ height: 280, width: '100%' }} />
            ) : (
              <Box className="text-muted small pt-5 text-center">No ontology domains available.</Box>
            )}
          </Paper>
        </Col>
        <Col lg={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              height: 360
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              LLM abuse drift
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Abuse score percent by event index when drift telemetry exists.
            </Typography>
            {abuseLineOption ? (
              <ReactEChartsCore echarts={echarts} option={abuseLineOption} style={{ height: 280, width: '100%' }} />
            ) : (
              <Box className="text-muted small pt-5 text-center">No drift series available.</Box>
            )}
          </Paper>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              height: 360
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Bias verdict distribution
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Counts from aggregate bias summary when provided by the pipeline.
            </Typography>
            {biasBarOption ? (
              <ReactEChartsCore echarts={echarts} option={biasBarOption} style={{ height: 280, width: '100%' }} />
            ) : (
              <Box className="text-muted small pt-5 text-center">No verdict distribution available.</Box>
            )}
          </Paper>
        </Col>
        <Col lg={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              height: 360
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              OWASP control status
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Pass vs fail per control (binary view of threshold checks).
            </Typography>
            {owaspBarOption ? (
              <ReactEChartsCore echarts={echarts} option={owaspBarOption} style={{ height: 280, width: '100%' }} />
            ) : (
              <Box className="text-muted small pt-5 text-center">No OWASP controls list available.</Box>
            )}
          </Paper>
        </Col>
      </Row>

      <Typography variant="h6" className="mb-3" fontWeight={700}>
        Detailed results
      </Typography>
      <Accordion flush className="border rounded overflow-hidden">
        <Accordion.Item eventKey="abuse">
          <Accordion.Header>Abuse analysis</Accordion.Header>
          <Accordion.Body>
            <p className="small text-muted mb-2">
              High-severity ML abuse rows (quick signal):{' '}
              <Badge bg={insights.abuse.mlHighRiskCount ? 'warning' : 'success'}>
                {insights.abuse.mlHighRiskCount}
              </Badge>
            </p>
            <dl className="row small mb-0">
              <dt className="col-sm-3">LLM verdict</dt>
              <dd className="col-sm-9">{insights.abuse.overallVerdict ?? '—'}</dd>
              <dt className="col-sm-3">Abuse index</dt>
              <dd className="col-sm-9">
                {insights.abuse.overallAbuseIndex != null ? insights.abuse.overallAbuseIndex.toFixed(2) : '—'}
              </dd>
            </dl>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="bias">
          <Accordion.Header>Bias</Accordion.Header>
          <Accordion.Body>
            <dl className="row small mb-0">
              <dt className="col-sm-3">Dataset verdict</dt>
              <dd className="col-sm-9">{insights.bias.datasetVerdict ?? '—'}</dd>
              <dt className="col-sm-3">MicroEAI score</dt>
              <dd className="col-sm-9">
                {insights.bias.microeaiBiasScore != null ? insights.bias.microeaiBiasScore.toFixed(3) : '—'}
              </dd>
              <dt className="col-sm-3">Sample LLM verdict</dt>
              <dd className="col-sm-9">{insights.bias.llmVerdictSample ?? '—'}</dd>
            </dl>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="fairness">
          <Accordion.Header>Fairness</Accordion.Header>
          <Accordion.Body>
            <p className="small fw-semibold mb-1">LLM fairness rows ({insights.fairness.llmRows.length})</p>
            <pre
              className="small bg-light dark__bg-opacity-10 p-2 rounded border mb-3"
              style={{ maxHeight: 180, overflow: 'auto' }}
            >
              {JSON.stringify(insights.fairness.llmRows.slice(0, 3), null, 2)}
            </pre>
            <p className="small fw-semibold mb-1">ML fairness rows ({insights.fairness.mlRows.length})</p>
            <pre
              className="small bg-light dark__bg-opacity-10 p-2 rounded border mb-0"
              style={{ maxHeight: 180, overflow: 'auto' }}
            >
              {JSON.stringify(insights.fairness.mlRows.slice(0, 5), null, 2)}
            </pre>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="explainability">
          <Accordion.Header>Explainability</Accordion.Header>
          <Accordion.Body>
            <dl className="row small mb-0">
              <dt className="col-sm-3">LLM SHAP</dt>
              <dd className="col-sm-9">
                {insights.explainability.llmShap != null ? insights.explainability.llmShap.toFixed(4) : '—'}
              </dd>
              <dt className="col-sm-3">ML SHAP</dt>
              <dd className="col-sm-9">
                {insights.explainability.mlShap != null ? insights.explainability.mlShap.toFixed(4) : '—'}
              </dd>
              <dt className="col-sm-3">MicroEAI verdict</dt>
              <dd className="col-sm-9">{insights.explainability.llmVerdict ?? '—'}</dd>
              <dt className="col-sm-3">LIME stability</dt>
              <dd className="col-sm-9">{insights.explainability.limeStability ?? '—'}</dd>
            </dl>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="compliance">
          <Accordion.Header>Compliance</Accordion.Header>
          <Accordion.Body>
            <dl className="row small mb-2">
              <dt className="col-sm-3">External</dt>
              <dd className="col-sm-9">{insights.compliance.externalStatus ?? '—'}</dd>
              <dt className="col-sm-3">Internal</dt>
              <dd className="col-sm-9">{insights.compliance.internalStatus ?? '—'}</dd>
            </dl>
            <p className="small fw-semibold mb-1">External findings ({insights.compliance.externalRows.length})</p>
            <pre
              className="small bg-light dark__bg-opacity-10 p-2 rounded border mb-3"
              style={{ maxHeight: 160, overflow: 'auto' }}
            >
              {JSON.stringify(insights.compliance.externalRows.slice(0, 4), null, 2)}
            </pre>
            <p className="small fw-semibold mb-1">Internal findings ({insights.compliance.internalRows.length})</p>
            <pre
              className="small bg-light dark__bg-opacity-10 p-2 rounded border mb-0"
              style={{ maxHeight: 160, overflow: 'auto' }}
            >
              {JSON.stringify(insights.compliance.internalRows.slice(0, 4), null, 2)}
            </pre>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="ontology">
          <Accordion.Header>Ontology</Accordion.Header>
          <Accordion.Body>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.ontologyDomains.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-muted small">
                        No ontology domains.
                      </td>
                    </tr>
                  ) : (
                    insights.ontologyDomains.map(d => (
                      <tr key={d.key}>
                        <td>{d.risk_domain}</td>
                        <td>
                          <Badge bg={riskBadgeVariant(d.risk_level)}>{d.risk_level}</Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="owasp">
          <Accordion.Header>OWASP MLP</Accordion.Header>
          <Accordion.Body>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Control</th>
                    <th>Status</th>
                    <th>Severity</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.owasp.controls.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-muted small">
                        No controls.
                      </td>
                    </tr>
                  ) : (
                    insights.owasp.controls.map(c => (
                      <tr key={c.name}>
                        <td>{c.name}</td>
                        <td>
                          <Badge bg={c.status?.toUpperCase() === 'PASS' ? 'success' : 'danger'}>{c.status}</Badge>
                        </td>
                        <td>{c.severity}</td>
                        <td>
                          {c.score.toFixed(2)} / {c.threshold.toFixed(2)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="audio">
          <Accordion.Header>Audio deepfake</Accordion.Header>
          <Accordion.Body>
            <dl className="row small mb-2">
              <dt className="col-sm-3">Decision</dt>
              <dd className="col-sm-9">{insights.audio.decision ?? '—'}</dd>
              <dt className="col-sm-3">Deepfake risk</dt>
              <dd className="col-sm-9">
                {insights.audio.deepfakeRisk != null ? insights.audio.deepfakeRisk.toFixed(2) : '—'}
              </dd>
              <dt className="col-sm-3">Policy hits</dt>
              <dd className="col-sm-9">
                {insights.audio.policyViolations.length ? insights.audio.policyViolations.join(', ') : '—'}
              </dd>
            </dl>
            {insights.audio.transcriptSnippet ? (
              <>
                <p className="small fw-semibold mb-1">Transcript (snippet)</p>
                <pre
                  className="small bg-light dark__bg-opacity-10 p-2 rounded border mb-0"
                  style={{ maxHeight: 140, overflow: 'auto', whiteSpace: 'pre-wrap' }}
                >
                  {insights.audio.transcriptSnippet}
                </pre>
              </>
            ) : null}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default ProjectOverviewDashboard;
